import React, { useMemo, useState } from "react";

import navLinksCatalog from "../../data/guidanceNavLinksRegistry.json";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  GuidanceNavLinkDto,
  createGuidanceNavLink,
  updateGuidanceNavLink,
} from "../../Store/Reducers/GuidanceSlice";
import { toast } from "../../Utils/Helper/toast";

type CatalogLink = {
  key: string;
  label: string;
  path: string;
  description?: string;
  active?: boolean;
};

const CATALOG_LINKS = (navLinksCatalog as { links: CatalogLink[] }).links;

const StatusPill: React.FC<{
  active: boolean;
  onClick?: () => void;
  disabled?: boolean;
}> = ({ active, onClick, disabled }) => (
  <span
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={disabled ? undefined : onClick}
    onKeyDown={
      onClick
        ? e => {
            if (!disabled && (e.key === "Enter" || e.key === " ")) onClick();
          }
        : undefined
    }
    className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1 border whitespace-nowrap ${
      active
        ? "bg-green-50 text-green-700 border-green-200"
        : "bg-gray-50 text-gray-400 border-gray-200"
    } ${onClick && !disabled ? "cursor-pointer hover:opacity-90" : ""}`}
  >
    {active ? "● Active" : "○ Inactive"}
  </span>
);

/**
 * Nav Links Registry — Add picks from the fixed route catalog only; Edit
 * label stays free-form, path is constrained to catalog routes.
 */
const NavLinksRegistryTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const { navLinks, navLinksLoading, savingKey } = useAppSelector(
    state => state.guidance
  );

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editPath, setEditPath] = useState("");
  const [editError, setEditError] = useState("");

  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedCatalogKey, setSelectedCatalogKey] = useState("");
  const [addError, setAddError] = useState("");

  const registeredKeys = useMemo(
    () => new Set(navLinks.map(link => link.key)),
    [navLinks]
  );

  const availableCatalogLinks = useMemo(
    () => CATALOG_LINKS.filter(link => !registeredKeys.has(link.key)),
    [registeredKeys]
  );

  const selectedCatalogLink = useMemo(
    () => CATALOG_LINKS.find(link => link.key === selectedCatalogKey) || null,
    [selectedCatalogKey]
  );

  const startEdit = (link: GuidanceNavLinkDto) => {
    setEditingKey(link.key);
    setEditLabel(link.label);
    setEditPath(link.path);
    setEditError("");
    setShowAddForm(false);
  };
  const cancelEdit = () => setEditingKey(null);

  const saveEdit = (key: string) => {
    const label = editLabel.trim();
    const path = editPath.trim();
    if (!label) {
      setEditError("Label is required.");
      return;
    }
    if (!CATALOG_LINKS.some(link => link.path === path)) {
      setEditError("Path must be one of the available routes.");
      return;
    }
    dispatch(updateGuidanceNavLink({ key, fields: { label, path } }));
    setEditingKey(null);
  };

  const toggleActive = (link: GuidanceNavLinkDto) => {
    dispatch(
      updateGuidanceNavLink({ key: link.key, fields: { active: !link.active } })
    );
  };

  const openAddForm = () => {
    setEditingKey(null);
    setAddError("");
    if (availableCatalogLinks.length === 0) {
      setShowAddForm(false);
      setSelectedCatalogKey("");
      toast.error(
        "All available routes have already been added to the registry. There are no routes left to add."
      );
      return;
    }
    setShowAddForm(true);
    setSelectedCatalogKey(availableCatalogLinks[0].key);
  };

  const cancelAdd = () => {
    setShowAddForm(false);
    setSelectedCatalogKey("");
    setAddError("");
  };

  const saveAdd = () => {
    if (!selectedCatalogLink) {
      setAddError("Select a route from the dropdown.");
      return;
    }
    if (registeredKeys.has(selectedCatalogLink.key)) {
      setAddError("This route is already in the registry.");
      return;
    }
    dispatch(
      createGuidanceNavLink({
        key: selectedCatalogLink.key,
        label: selectedCatalogLink.label,
        path: selectedCatalogLink.path,
        active: true,
      })
    );
    setShowAddForm(false);
    setSelectedCatalogKey("");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Nav Links Registry
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Manage the routes available as playbook chips.{" "}
            <span className="text-gray-400">
              {navLinks.length} of {CATALOG_LINKS.length} routes registered
            </span>
          </p>
        </div>
        {!showAddForm && (
          <button
            type="button"
            onClick={openAddForm}
            className="shrink-0 inline-flex items-center text-sm font-semibold text-white bg-[#974ae4] hover:bg-[#8538d4] rounded-lg px-4 py-2 transition-colors"
          >
            + Add link
          </button>
        )}
      </div>

      {showAddForm && (
        <section className="rounded-xl border border-purple-200 bg-purple-50/40 px-5 py-4">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <h4 className="text-sm font-semibold text-gray-900">Add link</h4>
              <p className="mt-0.5 text-xs text-gray-500">
                Select a route from the available catalog.
              </p>
            </div>
            <span className="text-xs font-medium text-gray-400">
              {availableCatalogLinks.length} available
            </span>
          </div>

          <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Route
              </label>
              <select
                autoFocus
                value={selectedCatalogKey}
                onChange={e => {
                  setSelectedCatalogKey(e.target.value);
                  setAddError("");
                }}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300"
              >
                {availableCatalogLinks.map(catalogLink => (
                  <option key={catalogLink.key} value={catalogLink.key}>
                    {catalogLink.label} — {catalogLink.path}
                  </option>
                ))}
              </select>
              {selectedCatalogLink && (
                <p className="mt-1.5 text-xs text-gray-500">
                  Key:{" "}
                  <span className="font-mono text-gray-700">
                    {selectedCatalogLink.key}
                  </span>
                </p>
              )}
              {addError && (
                <p className="mt-1.5 text-xs text-red-600">{addError}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={saveAdd}
                className="text-sm font-semibold text-white bg-[#974ae4] hover:bg-[#8538d4] rounded-lg px-4 py-2 transition-colors"
              >
                Add
              </button>
              <button
                type="button"
                onClick={cancelAdd}
                className="text-sm font-semibold text-gray-600 border border-gray-200 bg-white rounded-lg px-4 py-2 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </section>
      )}

      <section className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                <th className="px-5 py-3">Key</th>
                <th className="px-5 py-3">Label</th>
                <th className="px-5 py-3">Path</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {navLinksLoading && navLinks.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-5 py-10 text-center text-sm text-gray-400"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!navLinksLoading && navLinks.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-5 py-10 text-center text-sm text-gray-400"
                  >
                    No nav links registered yet. Click + Add link to get
                    started.
                  </td>
                </tr>
              )}
              {navLinks.map(link =>
                editingKey === link.key ? (
                  <tr key={link.key} className="bg-amber-50/70">
                    <td className="px-5 py-3 font-mono text-xs text-gray-500">
                      {link.key}
                    </td>
                    <td className="px-5 py-3">
                      <input
                        autoFocus
                        value={editLabel}
                        onChange={e => setEditLabel(e.target.value)}
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300"
                      />
                    </td>
                    <td className="px-5 py-3">
                      <select
                        value={editPath}
                        onChange={e => {
                          setEditPath(e.target.value);
                          setEditError("");
                        }}
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300"
                      >
                        {!CATALOG_LINKS.some(c => c.path === editPath) && (
                          <option value={editPath}>{editPath}</option>
                        )}
                        {CATALOG_LINKS.map(catalogLink => (
                          <option
                            key={catalogLink.key}
                            value={catalogLink.path}
                          >
                            {catalogLink.label} — {catalogLink.path}
                          </option>
                        ))}
                      </select>
                      {editError && (
                        <p className="text-xs text-red-600 mt-1">{editError}</p>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <StatusPill active={link.active} />
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-right">
                      <button
                        type="button"
                        onClick={() => saveEdit(link.key)}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#974ae4] text-white hover:bg-[#8538d4]"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 ml-1.5 hover:bg-white"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                ) : (
                  <tr key={link.key} className="hover:bg-gray-50/60">
                    <td className="px-5 py-3 font-mono text-xs text-gray-500">
                      {link.key}
                    </td>
                    <td className="px-5 py-3 font-medium text-gray-900">
                      {link.label}
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-gray-500">
                      {link.path}
                    </td>
                    <td className="px-5 py-3">
                      <StatusPill
                        active={link.active}
                        onClick={() => toggleActive(link)}
                        disabled={savingKey === link.key}
                      />
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => startEdit(link)}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:border-purple-300 hover:text-[#974ae4] hover:bg-purple-50"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default NavLinksRegistryTab;
